import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { RedImmobile, RedProfessionista, RedProfessionistaImmobile, RedGruppoImmobiliare, RedCondominoQuota, RedPraticaUSR, RedDocumento, RedDatoCatastale } from '../../../types/red'
import { EUR, FLAG_PROGETTAZIONE_USR, FlagProgettazioneVoce } from '../../../constants/red'
import { redApi, praticheUsrApi } from '../../../services/api'
import SelectPraticaUSR, { SelectPraticaUSRValue } from '../../../components/red/SelectPraticaUSR'
import NuovoProfessionistaModal from '../../../components/red/NuovoProfessionistaModal'
import UploadDocumentoFlagModal from './UploadDocumentoFlagModal'

type CostoExtraRow = { id: string; voce: string; importo: number }

type FormState = Record<string, string | CostoExtraRow[]>

// Elenco esplicito dei campi stringa di FormState diversi da `costi_extra`
// (che è un array di oggetti, non una stringa). Serve a impedire a
// compile-time che <Field field="costi_extra" .../> venga passato per errore:
// FormState resta un Record generico (serve alla lettura dinamica dei flag
// "Progettazione e USR" in CheckFieldDoc), quindi `keyof FormState` da solo
// non basta a escludere `costi_extra` — va elencato esplicitamente qui.
type StringFormField =
  | 'nome_riferimento' | 'indirizzo' | 'comune' | 'provincia' | 'cap' | 'foglio'
  | 'particella' | 'subalterno' | 'categoria_catastale' | 'superficie_mq' | 'localita'
  | 'uso' | 'classe_energetica' | 'rendita_catastale' | 'reddito_dominicale'
  | 'reddito_agrario' | 'vani' | 'piani' | 'stato_contrattuale' | 'data_compromesso'
  | 'scadenza_compromesso' | 'data_rogito' | 'notaio_id' | 'prezzo_acquisto'
  | 'acconto_pagato' | 'data_acconto' | 'acconto_pagato_il' | 'saldo_residuo'
  | 'data_saldo' | 'saldo_effettuato_il' | 'livello_operativo' | 'livello_danno'
  | 'ufficio_tecnico_id' | 'stato_di_fatto' | 'progetto_architettonico'
  | 'progetto_strutturale' | 'progetto_acustica' | 'relazione_geologica' | 'cme'
  | 'qte' | 'codice_fascicolo' | 'protocollo_usr' | 'delibera' | 'decreto_contributo'
  | 'importo_contributo' | 'accollo' | 'privato_o_condominio' | 'nome_condominio'
  | 'istruttore_pratica' | 'scheda_fast' | 'scheda_aedes' | 'planimetria'
  | 'totale_imposte' | 'totale_spese_notarili' | 'gruppo_id' | 'note'
  | 'ruolo'

const Field: React.FC<{
  label: string
  field: StringFormField
  type?: string
  form: FormState
  onChange: (field: string, value: string) => void
  disabled?: boolean
  hint?: string
}> = ({ label, field, type = 'text', form, onChange, disabled, hint }) => (
  <div>
    <label className="label">{label}</label>
    <input
      type={type}
      className={`input${type === 'number' ? ' no-spinner' : ''}`}
      value={(form[field] as string) ?? ''}
      onChange={e => onChange(field, e.target.value)}
      disabled={disabled}
    />
    {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
  </div>
)

const CheckFieldDoc: React.FC<{
  label: string
  field: string
  form: FormState
  onChange: (field: string, value: string) => void
  stato?: 'presente' | 'mancante'
  onIndicatorClick: () => void
}> = ({ label, field, form, onChange, stato, onIndicatorClick }) => (
  <label className="flex items-center gap-2 cursor-pointer">
    <input
      type="checkbox"
      checked={form[field] === 'true'}
      onChange={e => onChange(field, String(e.target.checked))}
      className="w-4 h-4"
    />
    <span className="text-sm text-gray-700">{label}</span>
    {stato && (
      <button
        type="button"
        onClick={e => { e.preventDefault(); onIndicatorClick() }}
        title={stato === 'presente' ? 'Documento presente — clic per scaricare/sostituire' : 'Documento mancante — clic per caricare'}
        className="text-sm leading-none"
      >
        {stato === 'presente' ? '📎' : '⚠️'}
      </button>
    )}
  </label>
)

const TabAnagrafica: React.FC<{ immobile: RedImmobile; onUpdate: () => void }> = ({ immobile, onUpdate }) => {
  const navigate = useNavigate()
  const [form, setForm] = useState<FormState>(() => {
    const init: FormState = {
      nome_riferimento: immobile.nome_riferimento ?? '',
      indirizzo: immobile.indirizzo ?? '',
      comune: immobile.comune ?? '',
      provincia: immobile.provincia ?? 'MC',
      cap: immobile.cap ?? '',
      foglio: immobile.foglio ?? '',
      particella: immobile.particella ?? '',
      subalterno: immobile.subalterno ?? '',
      categoria_catastale: immobile.categoria_catastale ?? '',
      superficie_mq: immobile.superficie_mq != null ? String(immobile.superficie_mq) : '',
      localita: immobile.localita ?? '',
      uso: immobile.uso ?? '',
      classe_energetica: immobile.classe_energetica ?? '',
      rendita_catastale: immobile.rendita_catastale != null ? String(immobile.rendita_catastale) : '',
      reddito_dominicale: immobile.reddito_dominicale != null ? String(immobile.reddito_dominicale) : '',
      reddito_agrario: immobile.reddito_agrario != null ? String(immobile.reddito_agrario) : '',
      vani: immobile.vani != null ? String(immobile.vani) : '',
      piani: immobile.piani ?? '',
      stato_contrattuale: immobile.stato_contrattuale ?? 'trattativa',
      data_compromesso: immobile.data_compromesso ?? '',
      scadenza_compromesso: immobile.scadenza_compromesso ?? '',
      data_rogito: immobile.data_rogito ?? '',
      notaio_id: immobile.notaio_id ?? '',
      prezzo_acquisto: immobile.prezzo_acquisto != null ? String(immobile.prezzo_acquisto) : '',
      acconto_pagato: immobile.acconto_pagato != null ? String(immobile.acconto_pagato) : '',
      data_acconto: immobile.data_acconto ?? '',
      acconto_pagato_il: immobile.acconto_pagato_il ?? '',
      saldo_residuo: immobile.saldo_residuo != null ? String(immobile.saldo_residuo) : '',
      data_saldo: immobile.data_saldo ?? '',
      saldo_effettuato_il: immobile.saldo_effettuato_il ?? '',
      livello_operativo: immobile.livello_operativo ?? '',
      livello_danno: immobile.livello_danno ?? '',
      ufficio_tecnico_id: immobile.ufficio_tecnico_id ?? '',
      stato_di_fatto: String(immobile.stato_di_fatto ?? false),
      progetto_architettonico: String(immobile.progetto_architettonico ?? false),
      progetto_strutturale: String(immobile.progetto_strutturale ?? false),
      progetto_acustica: String(immobile.progetto_acustica ?? false),
      relazione_geologica: String(immobile.relazione_geologica ?? false),
      cme: String(immobile.cme ?? false),
      qte: String(immobile.qte ?? false),
      codice_fascicolo: immobile.codice_fascicolo ?? '',
      protocollo_usr: immobile.protocollo_usr ?? '',
      delibera: immobile.delibera ?? '',
      decreto_contributo: immobile.decreto_contributo ?? '',
      importo_contributo: immobile.importo_contributo != null ? String(immobile.importo_contributo) : '',
      accollo: String(immobile.accollo ?? false),
      privato_o_condominio: immobile.privato_o_condominio ?? '',
      nome_condominio: immobile.nome_condominio ?? '',
      istruttore_pratica: immobile.istruttore_pratica ?? '',
      scheda_fast: String(immobile.scheda_fast ?? false),
      scheda_aedes: String(immobile.scheda_aedes ?? false),
      planimetria: String(immobile.planimetria ?? false),
      totale_imposte: immobile.totale_imposte != null ? String(immobile.totale_imposte) : '',
      totale_spese_notarili: immobile.totale_spese_notarili != null ? String(immobile.totale_spese_notarili) : '',
      gruppo_id: immobile.gruppo_id ?? '',
      costi_extra: immobile.costi_extra ?? [],
      note: immobile.note ?? '',
    }
    return init
  })
  const [saving, setSaving] = useState(false)
  const [editLat, setEditLat] = useState(immobile.lat != null ? String(immobile.lat) : '')
  const [editLon, setEditLon] = useState(immobile.lon != null ? String(immobile.lon) : '')
  const [geocoding, setGeocoding] = useState(false)
  const [savingCoord, setSavingCoord] = useState(false)

  useEffect(() => {
    setEditLat(immobile.lat != null ? String(immobile.lat) : '')
    setEditLon(immobile.lon != null ? String(immobile.lon) : '')
  }, [immobile.lat, immobile.lon])

  const handleGeocode = async () => {
    if (!immobile.comune && !immobile.indirizzo) {
      toast.error('Inserisci almeno Comune o Indirizzo prima di cercare le coordinate')
      return
    }
    setGeocoding(true)
    try {
      const res = await redApi.immobili.geocode(immobile.id)
      const { lat: newLat, lon: newLon } = res.data as { lat: number; lon: number }
      setEditLat(String(newLat))
      setEditLon(String(newLon))
      toast.success('Coordinate trovate automaticamente')
      onUpdate()
    } catch (err: any) {
      if (err?.response?.status === 422) {
        toast.error('Indirizzo non trovato: verifica Indirizzo, Comune, Provincia e CAP')
      } else {
        toast.error('Errore durante la ricerca automatica delle coordinate')
      }
    } finally {
      setGeocoding(false)
    }
  }

  const handleSaveCoord = async () => {
    const newLat = parseFloat(editLat)
    const newLon = parseFloat(editLon)
    if (isNaN(newLat) || isNaN(newLon)) {
      toast.error('Coordinate non valide')
      return
    }
    setSavingCoord(true)
    try {
      await redApi.mappa.aggiornaCoordinate(immobile.id, newLat, newLon)
      toast.success('Coordinate aggiornate')
      onUpdate()
    } catch {
      toast.error('Errore salvataggio coordinate')
    } finally {
      setSavingCoord(false)
    }
  }
  const [professionisti, setProfessionisti] = useState<RedProfessionista[]>([])
  const [profAssoc, setProfAssoc] = useState<RedProfessionistaImmobile[]>([])
  const [showAddProf, setShowAddProf] = useState(false)
  const [selectedProf, setSelectedProf] = useState('')
  const [selectedRuolo, setSelectedRuolo] = useState('')
  const [showNuovoUfficioTecnico, setShowNuovoUfficioTecnico] = useState(false)
  const [showNuovoNotaio, setShowNuovoNotaio] = useState(false)
  const [showNuovoProfessionista, setShowNuovoProfessionista] = useState(false)
  const [gruppo, setGruppo] = useState<RedGruppoImmobiliare | null>(null)
  const [condomini, setCondomini] = useState<RedCondominoQuota[]>([])
  const [editingCondominoId, setEditingCondominoId] = useState<string | null>(null)
  const [condominoForm, setCondominoForm] = useState({ nome_condomino: '', percentuale: '', note: '' })
  const sommaPercentuali = condomini.reduce((s, c) => s + (Number(c.percentuale) || 0), 0)
  const [condoConfig, setCondoConfig] = useState({
    nome_condominio: '',
    selectedImmobili: [] as string[],
    praticaModo: 'nuova' as 'nessuna' | 'nuova' | 'esistente',
    praticaNumero: '',
    praticaImporto: '',
    praticaEsistenteId: '',
    quotaPercentuale: '',
  })
  const [allImmobili, setAllImmobili] = useState<RedImmobile[]>([])
  const [praticheDisponibili, setPraticheDisponibili] = useState<RedPraticaUSR[]>([])
  const [documenti, setDocumenti] = useState<RedDocumento[]>([])
  const [datiCatastali, setDatiCatastali] = useState<RedDatoCatastale[]>([])
  const [modalVoce, setModalVoce] = useState<FlagProgettazioneVoce | null>(null)
  const [modalDocumentiEsistenti, setModalDocumentiEsistenti] = useState<RedDocumento[]>([])
  const VOCE_DISTINTA_ACCONTO: FlagProgettazioneVoce = { label: 'Distinta acconto', tipologia: 'distinta_acconto' }
  const VOCE_DISTINTA_SALDO: FlagProgettazioneVoce = { label: 'Distinta saldo', tipologia: 'distinta_saldo' }
  const [praticaValue, setPraticaValue] = useState<SelectPraticaUSRValue>({
    pratica_usr_id: immobile.pratica_usr_id,
    quota_percentuale: immobile.usr_quota_percentuale,
  })

  useEffect(() => {
    redApi.professionisti.list().then(r => setProfessionisti(r.data))
    redApi.professionisti.listByImmobile(immobile.id).then(r => setProfAssoc(r.data))
  }, [immobile.id])

  useEffect(() => {
    if (immobile.gruppo_id) {
      redApi.gruppi.get(immobile.gruppo_id).then(r => setGruppo(r.data)).catch(() => setGruppo(null))
    } else {
      setGruppo(null)
    }
  }, [immobile.gruppo_id])

  useEffect(() => {
    if (gruppo?.is_condominio) {
      redApi.gruppi.listCondomini(gruppo.id).then(r => setCondomini(r.data)).catch(() => setCondomini([]))
    } else {
      setCondomini([])
    }
  }, [gruppo?.id, gruppo?.is_condominio])

  useEffect(() => {
    redApi.immobili.list().then(r => {
      setAllImmobili(r.data.filter((i: RedImmobile) => i.id !== immobile.id && !i.gruppo_id))
    }).catch(() => {})
  }, [immobile.id])

  useEffect(() => {
    praticheUsrApi.list().then(r => setPraticheDisponibili(r.data)).catch(() => {})
  }, [])

  // Sincronizza praticaValue quando l'immobile viene ricaricato (es. dopo save)
  useEffect(() => {
    setPraticaValue({
      pratica_usr_id: immobile.pratica_usr_id,
      quota_percentuale: immobile.usr_quota_percentuale,
    })
  }, [immobile.pratica_usr_id, immobile.usr_quota_percentuale])

  const handleChange = (field: string, value: string | CostoExtraRow[]) => {
    setForm(prev => {
      const next = { ...prev, [field]: value }
      // Saldo residuo è sempre calcolato: prezzo di acquisto - acconto pagato
      // (il campo mostrato in UI resta disabilitato, qui si tiene solo allineato).
      if (field === 'prezzo_acquisto' || field === 'acconto_pagato') {
        const prezzo = parseFloat(next.prezzo_acquisto as string)
        const acconto = parseFloat(next.acconto_pagato as string) || 0
        next.saldo_residuo = Number.isFinite(prezzo) ? String(prezzo - acconto) : ''
      }
      return next
    })
  }

  const loadDocumenti = () => {
    redApi.documenti.listByImmobile(immobile.id).then(r => setDocumenti(r.data)).catch(() => {})
  }

  useEffect(loadDocumenti, [immobile.id])

  const loadDatiCatastali = () => {
    redApi.datiCatastali.listByImmobile(immobile.id).then(r => setDatiCatastali(r.data)).catch(() => {})
  }

  useEffect(loadDatiCatastali, [immobile.id])

  const handleAggiungiVoceCatastale = async () => {
    const nome = prompt('Nome della voce catastale (es. "Casa", "Terreno", "Cantina"):')
    if (!nome) return
    try {
      await redApi.datiCatastali.create(immobile.id, { nome_voce: nome, ordine: datiCatastali.length })
      loadDatiCatastali()
    } catch {
      toast.error('Errore creazione voce catastale')
    }
  }

  const handleUpdateVoceCatastale = async (voceId: string, field: string, value: string) => {
    const numeriche = ['superficie_mq', 'vani', 'rendita_catastale', 'reddito_dominicale', 'reddito_agrario']
    const payload: Record<string, unknown> = {
      [field]: value === '' ? null : (numeriche.includes(field) ? parseFloat(value) : value),
    }
    try {
      await redApi.datiCatastali.update(voceId, payload)
      setDatiCatastali(prev => prev.map(v => v.id === voceId ? { ...v, [field]: payload[field] } : v))
    } catch {
      toast.error('Errore aggiornamento voce catastale')
    }
  }

  const handleDeleteVoceCatastale = async (voceId: string) => {
    if (!confirm('Eliminare questa voce catastale?')) return
    try {
      await redApi.datiCatastali.delete(voceId)
      setDatiCatastali(prev => prev.filter(v => v.id !== voceId))
      toast.success('Voce catastale eliminata')
    } catch {
      toast.error('Errore eliminazione voce catastale')
    }
  }

  const documentiPerTipologia = (tipologia: string): RedDocumento[] =>
    documenti
      .filter(d => d.tipologia === tipologia && d.caricato)
      .sort((a, b) => (b.caricato_at || '').localeCompare(a.caricato_at || ''))

  // Bug #11: spuntare uno dei flag "Progettazione e USR" apre il modale di
  // caricamento documento, ma il flag resta comunque salvabile senza upload
  // ("Più tardi") — vedi docs/superpowers/specs/2026-07-29-flag-progettazione-usr-upload-documento-design.md
  const handleFlagChange = (field: string, value: string) => {
    handleChange(field, value)
    if (value === 'true') {
      const voce = FLAG_PROGETTAZIONE_USR.find(v => v.field === field)
      if (voce) {
        setModalDocumentiEsistenti(documentiPerTipologia(voce.tipologia))
        setModalVoce(voce)
      }
    }
  }

  const openIndicatorModal = (voce: FlagProgettazioneVoce) => {
    setModalDocumentiEsistenti(documentiPerTipologia(voce.tipologia))
    setModalVoce(voce)
  }

  const handleSave = async () => {
    if (form.acconto_pagato_il && documentiPerTipologia('distinta_acconto').length === 0) {
      toast('Distinta acconto non ancora caricata', { icon: '⚠️' })
    }
    if (form.saldo_effettuato_il && documentiPerTipologia('distinta_saldo').length === 0) {
      toast('Distinta saldo non ancora caricata', { icon: '⚠️' })
    }
    setSaving(true)
    try {
      // 1. Salva i campi normali dell'immobile
      const payload: Record<string, unknown> = {}
      if (praticaValue.nuova_pratica) {
        payload.nuova_pratica_usr = praticaValue.nuova_pratica
      } else if (praticaValue.pratica_usr_id) {
        payload.pratica_usr_id = praticaValue.pratica_usr_id
      } else {
        payload.pratica_usr_id = null
      }
      if (praticaValue.quota_percentuale != null) {
        payload.usr_quota_percentuale = praticaValue.quota_percentuale
      }
      for (const [k, v] of Object.entries(form)) {
        if (k === 'gruppo_id') continue // gestito separatamente via creaCondominio/assegna gruppo
        if (v === '') payload[k] = null
        else if (['superficie_mq', 'rendita_catastale', 'reddito_dominicale', 'reddito_agrario', 'vani', 'prezzo_acquisto', 'acconto_pagato', 'saldo_residuo', 'importo_contributo', 'totale_imposte', 'totale_spese_notarili'].includes(k)) {
          payload[k] = v === null ? null : parseFloat(v as string)
        } else if (['stato_di_fatto', 'progetto_architettonico', 'progetto_strutturale', 'progetto_acustica', 'relazione_geologica', 'cme', 'qte', 'accollo', 'scheda_fast', 'scheda_aedes', 'planimetria'].includes(k)) {
          payload[k] = v === 'true'
        } else {
          payload[k] = v
        }
      }
      await redApi.immobili.update(immobile.id, payload)
      toast.success('Anagrafica aggiornata')

      // 2. Se è in modalità creazione condominio, crea il gruppo
      if (form.privato_o_condominio === 'condominio' && !immobile.gruppo_id && condoConfig.nome_condominio) {
        const immobileIds = condoConfig.selectedImmobili
        await redApi.gruppi.creaCondominio({
          nome_condominio: condoConfig.nome_condominio,
          immobile_ids: immobileIds,
          pratica_usr: condoConfig.praticaModo === 'nuova' ? {
            numero_pratica: condoConfig.praticaNumero,
            importo_approvato: condoConfig.praticaImporto ? parseFloat(condoConfig.praticaImporto) : null,
          } : null,
          pratica_usr_id: condoConfig.praticaModo === 'esistente' ? condoConfig.praticaEsistenteId || null : null,
          immobile_corrente_id: immobile.id,
          immobile_corrente_quota: condoConfig.quotaPercentuale ? parseFloat(condoConfig.quotaPercentuale) : null,
        })
        toast.success('Condominio creato')
      }

      onUpdate()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const handleAddProf = async () => {
    if (!selectedProf) return
    try {
      await redApi.professionisti.addToImmobile(immobile.id, {
        professionista_id: selectedProf,
        ruolo_specifico: selectedRuolo || undefined,
      })
      toast.success('Professionista associato')
      setShowAddProf(false)
      setSelectedProf('')
      setSelectedRuolo('')
      const r = await redApi.professionisti.listByImmobile(immobile.id)
      setProfAssoc(r.data)
    } catch {
      toast.error('Errore associazione')
    }
  }

  // Bug #9: nuovo ufficio tecnico creato al volo → selezionato subito nel campo
  const handleUfficioTecnicoCreato = (p: RedProfessionista) => {
    setProfessionisti(prev => [...prev, p])
    handleChange('ufficio_tecnico_id', p.id)
  }

  // Stesso pattern per il notaio: nuovo notaio creato al volo → selezionato subito
  const handleNotaioCreato = (p: RedProfessionista) => {
    setProfessionisti(prev => [...prev, p])
    handleChange('notaio_id', p.id)
  }

  // Bug #13: nuovo professionista creato al volo → preselezionato nel form di associazione
  const handleProfessionistaCreato = (p: RedProfessionista) => {
    setProfessionisti(prev => [...prev, p])
    setSelectedProf(p.id)
  }

  const handleRemoveProf = async (assocId: string) => {
    if (!confirm('Rimuovere il professionista da questo immobile?')) return
    try {
      await redApi.professionisti.removeFromImmobile(immobile.id, assocId)
      toast.success('Professionista rimosso')
      const r = await redApi.professionisti.listByImmobile(immobile.id)
      setProfAssoc(r.data)
    } catch {
      toast.error('Errore rimozione')
    }
  }

  const creaGruppo = async () => {
    const nome_gruppo = prompt('Nome gruppo immobiliare (es. "Condominio Via Roma"):')
    if (!nome_gruppo) return
    try {
      const nuovoGruppo = await redApi.gruppi.create({ nome_gruppo })
      await redApi.immobili.update(immobile.id, { gruppo_id: nuovoGruppo.data.id })
      toast.success('Gruppo creato e assegnato')
      onUpdate()
    } catch {
      toast.error('Errore creazione gruppo')
    }
  }

  const handleAddCondomino = async () => {
    if (!condominoForm.nome_condomino || !condominoForm.percentuale) {
      toast.error('Nome e percentuale sono obbligatori')
      return
    }
    const pct = parseFloat(condominoForm.percentuale)
    if (isNaN(pct)) {
      toast.error('Inserire una percentuale valida')
      return
    }
    try {
      await redApi.gruppi.addCondomino(gruppo!.id, {
        nome_condomino: condominoForm.nome_condomino,
        percentuale: pct,
        note: condominoForm.note || undefined,
      })
      toast.success('Condomino aggiunto')
      setCondominoForm({ nome_condomino: '', percentuale: '', note: '' })
      const r = await redApi.gruppi.listCondomini(gruppo!.id)
      setCondomini(r.data)
      onUpdate()
    } catch {
      toast.error('Errore aggiunta condomino')
    }
  }

  const handleUpdateCondomino = async (id: string) => {
    if (!condominoForm.nome_condomino || !condominoForm.percentuale) {
      toast.error('Nome e percentuale sono obbligatori')
      return
    }
    const pct = parseFloat(condominoForm.percentuale)
    if (isNaN(pct)) {
      toast.error('Inserire una percentuale valida')
      return
    }
    try {
      await redApi.gruppi.updateCondomino(id, {
        nome_condomino: condominoForm.nome_condomino,
        percentuale: pct,
        note: condominoForm.note || undefined,
      })
      toast.success('Condomino aggiornato')
      setEditingCondominoId(null)
      setCondominoForm({ nome_condomino: '', percentuale: '', note: '' })
      const r = await redApi.gruppi.listCondomini(gruppo!.id)
      setCondomini(r.data)
      onUpdate()
    } catch {
      toast.error('Errore aggiornamento condomino')
    }
  }

  const handleDeleteCondomino = async (id: string) => {
    if (!confirm('Eliminare questo condomino?')) return
    try {
      await redApi.gruppi.deleteCondomino(id)
      toast.success('Condomino eliminato')
      const r = await redApi.gruppi.listCondomini(gruppo!.id)
      setCondomini(r.data)
      onUpdate()
    } catch {
      toast.error('Errore eliminazione condomino')
    }
  }

  const notai = professionisti.filter(p => p.tipo === 'notaio')
  const tecnici = professionisti.filter(p => p.tipo === 'tecnico')

  return (
    <div className="max-w-4xl space-y-6">
      {/* Dati generali */}
      <div className="card p-5">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Immobile</h2>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Nome immobile" field="nome_riferimento" form={form} onChange={handleChange} />
          <Field label="Indirizzo" field="indirizzo" form={form} onChange={handleChange} />
          <Field label="Comune" field="comune" form={form} onChange={handleChange} />
          <Field label="Provincia" field="provincia" form={form} onChange={handleChange} />
          <Field label="CAP" field="cap" form={form} onChange={handleChange} />
          <Field label="Località" field="localita" form={form} onChange={handleChange} />
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">📍 Georeferenziazione</h3>
          <div className="flex gap-2 items-center flex-wrap mb-3">
            <button type="button" onClick={handleGeocode} disabled={geocoding} className="btn-secondary text-sm">
              {geocoding ? 'Ricerca in corso...' : '🔍 Trova coordinate automaticamente'}
            </button>
            <span className="text-xs text-gray-400">da indirizzo, comune, provincia e CAP dell'immobile</span>
          </div>
          <div className="flex gap-3 items-end flex-wrap">
            <div>
              <label className="label">Latitudine</label>
              <input type="number" step="any" className="input w-40 no-spinner" value={editLat} onChange={e => setEditLat(e.target.value)} />
            </div>
            <div>
              <label className="label">Longitudine</label>
              <input type="number" step="any" className="input w-40 no-spinner" value={editLon} onChange={e => setEditLon(e.target.value)} />
            </div>
            <button type="button" onClick={handleSaveCoord} disabled={savingCoord} className="btn-primary text-sm">
              {savingCoord ? 'Salvataggio...' : 'Salva coordinate'}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            In alternativa puoi trovare le coordinate su <a href="https://www.openstreetmap.org" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">OpenStreetMap</a> cliccando con il tasto destro → "Mostra indirizzo" e inserirle qui manualmente.
          </p>
        </div>
      </div>

      {/* Dati catastali */}
      <div className="card p-5">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Dati catastali</h2>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Foglio" field="foglio" form={form} onChange={handleChange} />
          <Field label="Particella" field="particella" form={form} onChange={handleChange} />
          <Field label="Subalterno" field="subalterno" form={form} onChange={handleChange} />
          <Field label="Categoria" field="categoria_catastale" form={form} onChange={handleChange} />
          <Field label="Uso" field="uso" form={form} onChange={handleChange} />
          <Field label="Classe energetica" field="classe_energetica" form={form} onChange={handleChange} />
          <Field label="Superficie (mq)" field="superficie_mq" type="number" form={form} onChange={handleChange} />
          <Field label="Vani" field="vani" type="number" form={form} onChange={handleChange} />
          <Field label="Piani" field="piani" form={form} onChange={handleChange} />
          <Field label="Rendita catastale" field="rendita_catastale" type="number" form={form} onChange={handleChange} />
          <Field label="Reddito dominicale" field="reddito_dominicale" type="number" form={form} onChange={handleChange} />
          <Field label="Reddito agrario" field="reddito_agrario" type="number" form={form} onChange={handleChange} />
        </div>

        {datiCatastali.length > 0 && (
          <div className="mt-5 space-y-4">
            {datiCatastali.map(voce => (
              <div key={voce.id} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-800">{voce.nome_voce}</h3>
                  <button onClick={() => handleDeleteVoceCatastale(voce.id)} className="text-red-500 text-xs hover:underline">Elimina voce</button>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {([
                    ['foglio', 'Foglio', 'text'],
                    ['particella', 'Particella', 'text'],
                    ['subalterno', 'Subalterno', 'text'],
                    ['categoria_catastale', 'Categoria', 'text'],
                    ['uso', 'Uso', 'text'],
                    ['classe_energetica', 'Classe energetica', 'text'],
                    ['superficie_mq', 'Superficie (mq)', 'number'],
                    ['vani', 'Vani', 'number'],
                    ['piani', 'Piani', 'text'],
                    ['rendita_catastale', 'Rendita catastale', 'number'],
                    ['reddito_dominicale', 'Reddito dominicale', 'number'],
                    ['reddito_agrario', 'Reddito agrario', 'number'],
                  ] as const).map(([field, label, type]) => (
                    <div key={field}>
                      <label className="label">{label}</label>
                      <input
                        type={type}
                        className={`input${type === 'number' ? ' no-spinner' : ''}`}
                        defaultValue={(voce as unknown as Record<string, unknown>)[field] as string ?? ''}
                        onBlur={e => handleUpdateVoceCatastale(voce.id, field, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <button type="button" onClick={handleAggiungiVoceCatastale} className="btn-secondary text-sm mt-4">
          + Aggiungi voce catastale
        </button>
      </div>

      {/* Gruppo Immobiliare */}
      <div className="card p-5">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Gruppo Immobiliare</h2>
        {!immobile.gruppo_id ? (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">Immobile standalone (non raggruppato).</p>
            <button onClick={creaGruppo} className="btn-primary text-sm">Crea/Assegna gruppo</button>
          </div>
        ) : gruppo && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Gruppo: <strong>{gruppo.nome_gruppo}</strong> — {gruppo.n_immobili} immobili
              {gruppo.n_immobili > 1 && (
                <a href={`/red/gruppi/${gruppo.id}`} className="text-blue-600 hover:underline ml-2">vedi tutti</a>
              )}
            </p>
            <div>
              <label className="label">Dati generali — riferimenti</label>
              <textarea
                defaultValue={gruppo.riferimenti || ''}
                onBlur={e => redApi.gruppi.update(gruppo.id, { riferimenti: e.target.value }).then(onUpdate).catch(() => toast.error('Errore aggiornamento'))}
                className="input"
                rows={2}
                placeholder="Dati generali - riferimenti..."
              />
            </div>

            {/* Condominio */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Condominio</label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={gruppo.is_condominio}
                  onChange={e => redApi.gruppi.update(gruppo.id, { is_condominio: e.target.checked }).then(onUpdate).catch(() => toast.error('Errore aggiornamento'))}
                  className="w-4 h-4"
                />
                {gruppo.is_condominio ? 'SI' : 'NO'}
              </label>
            </div>
            {gruppo.is_condominio && (
              <div className="space-y-3 bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Amministratore di Condominio</label>
                    <input
                      defaultValue={gruppo.condominio_amministratore || ''}
                      onBlur={e => redApi.gruppi.update(gruppo.id, { condominio_amministratore: e.target.value }).then(onUpdate).catch(() => toast.error('Errore aggiornamento'))}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="label">Nome Condominio</label>
                    <input
                      defaultValue={gruppo.condominio_nome || ''}
                      onBlur={e => redApi.gruppi.update(gruppo.id, { condominio_nome: e.target.value }).then(onUpdate).catch(() => toast.error('Errore aggiornamento'))}
                      className="input"
                    />
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                      <th className="py-2">Nome</th>
                      <th className="py-2 text-right">%</th>
                      <th className="py-2">Note</th>
                      <th className="py-2 text-right">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {condomini.map(c => (
                      <tr key={c.id} className="border-b border-gray-200">
                        {editingCondominoId === c.id ? (
                          <>
                            <td className="py-2 pr-2">
                              <input className="input text-sm py-1" value={condominoForm.nome_condomino} onChange={e => setCondominoForm({ ...condominoForm, nome_condomino: e.target.value })} />
                            </td>
                            <td className="py-2 pr-2">
                              <input className="input text-sm py-1 text-right" type="number" step="0.01" value={condominoForm.percentuale} onChange={e => setCondominoForm({ ...condominoForm, percentuale: e.target.value })} />
                            </td>
                            <td className="py-2 pr-2">
                              <input className="input text-sm py-1" value={condominoForm.note} onChange={e => setCondominoForm({ ...condominoForm, note: e.target.value })} />
                            </td>
                            <td className="py-2 text-right">
                              <button onClick={() => handleUpdateCondomino(c.id)} className="text-green-600 text-xs hover:underline mr-2">Salva</button>
                              <button onClick={() => { setEditingCondominoId(null); setCondominoForm({ nome_condomino: '', percentuale: '', note: '' }) }} className="text-gray-500 text-xs hover:underline">Annulla</button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="py-2">{c.nome_condomino}</td>
                            <td className="py-2 text-right font-medium">{c.percentuale}%</td>
                            <td className="py-2 text-gray-500">{c.note}</td>
                            <td className="py-2 text-right">
                              <button onClick={() => { setEditingCondominoId(c.id); setCondominoForm({ nome_condomino: c.nome_condomino, percentuale: String(c.percentuale), note: c.note || '' }) }} className="text-blue-600 text-xs hover:underline mr-2">Modifica</button>
                              <button onClick={() => handleDeleteCondomino(c.id)} className="text-red-500 text-xs hover:underline">Elimina</button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                    {condomini.length === 0 && (
                      <tr><td className="py-4 text-gray-400 text-center" colSpan={4}>Nessun condomino inserito</td></tr>
                    )}
                  </tbody>
                </table>
                <div className="mt-3 grid grid-cols-4 gap-2 items-end">
                  <div>
                    <label className="label text-xs">Nome condomino</label>
                    <input className="input text-sm" value={condominoForm.nome_condomino} onChange={e => setCondominoForm({ ...condominoForm, nome_condomino: e.target.value })} placeholder="Nome..." />
                  </div>
                  <div>
                    <label className="label text-xs">Percentuale</label>
                    <input className="input text-sm" type="number" step="0.01" value={condominoForm.percentuale} onChange={e => setCondominoForm({ ...condominoForm, percentuale: e.target.value })} placeholder="%" />
                  </div>
                  <div>
                    <label className="label text-xs">Note</label>
                    <input className="input text-sm" value={condominoForm.note} onChange={e => setCondominoForm({ ...condominoForm, note: e.target.value })} placeholder="Note..." />
                  </div>
                  <button onClick={handleAddCondomino} className="btn-primary text-sm py-1.5">Aggiungi</button>
                </div>
                {sommaPercentuali !== 100 && (
                  <p className="text-xs text-amber-600 mt-2">Somma percentuali: {sommaPercentuali.toFixed(2)}% {sommaPercentuali > 100 ? '(superiore a 100%)' : '(inferiore a 100%)'}</p>
                )}
              </div>
            )}

            {/* Manifestazione di Interesse */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="label">Manifestazione — Nome Impresa</label>
                <input
                  defaultValue={gruppo.manifestazione_nome_impresa || ''}
                  onBlur={e => redApi.gruppi.update(gruppo.id, { manifestazione_nome_impresa: e.target.value }).then(onUpdate).catch(() => toast.error('Errore aggiornamento'))}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Data Invio</label>
                <input
                  type="date"
                  defaultValue={gruppo.manifestazione_data_invio || ''}
                  onBlur={e => redApi.gruppi.update(gruppo.id, { manifestazione_data_invio: e.target.value }).then(onUpdate).catch(() => toast.error('Errore aggiornamento'))}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Esito</label>
                <select
                  defaultValue={gruppo.manifestazione_esito || ''}
                  onChange={e => redApi.gruppi.update(gruppo.id, { manifestazione_esito: e.target.value }).then(onUpdate).catch(() => toast.error('Errore aggiornamento'))}
                  className="input"
                >
                  <option value="">—</option>
                  <option value="in_attesa">In attesa</option>
                  <option value="positivo">Positivo</option>
                  <option value="negativo">Negativo</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Configurazione Condominio */}
        {form.privato_o_condominio === 'condominio' && !immobile.gruppo_id && (
          <div className="bg-blue-50 rounded-lg p-4 space-y-4 mt-4">
            <h3 className="font-medium text-blue-900">Configura Condominio</h3>
            <div>
              <label className="label">Nome condominio</label>
              <input
                className="input"
                value={condoConfig.nome_condominio}
                onChange={e => setCondoConfig({ ...condoConfig, nome_condominio: e.target.value })}
                placeholder="Condominio Via Roma 10"
              />
            </div>
            <div>
              <label className="label">Seleziona immobili esistenti</label>
              <div className="max-h-40 overflow-y-auto bg-white rounded border p-2 space-y-1">
                {allImmobili.map(i => (
                  <label key={i.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={condoConfig.selectedImmobili.includes(i.id)}
                      onChange={e => {
                        const ids = e.target.checked
                          ? [...condoConfig.selectedImmobili, i.id]
                          : condoConfig.selectedImmobili.filter(id => id !== i.id)
                        setCondoConfig({ ...condoConfig, selectedImmobili: ids })
                      }}
                    />
                    <span>{i.nome_riferimento} — {i.comune} {i.indirizzo}</span>
                  </label>
                ))}
                {allImmobili.length === 0 && <p className="text-xs text-gray-400">Nessun immobile disponibile</p>}
              </div>
            </div>
            <div>
              <label className="label">Pratica USR del condominio</label>
              <select
                className="input"
                value={condoConfig.praticaModo}
                onChange={e => setCondoConfig({ ...condoConfig, praticaModo: e.target.value as typeof condoConfig.praticaModo })}
              >
                <option value="nessuna">Nessuna pratica USR</option>
                <option value="nuova">Crea nuova pratica USR</option>
                <option value="esistente">Collega pratica USR esistente</option>
              </select>
            </div>
            {condoConfig.praticaModo === 'nuova' && (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="label text-xs">Numero pratica</label>
                  <input className="input" value={condoConfig.praticaNumero} onChange={e => setCondoConfig({ ...condoConfig, praticaNumero: e.target.value })} />
                </div>
                <div>
                  <label className="label text-xs">Importo approvato (&euro;)</label>
                  <input type="number" className="input" value={condoConfig.praticaImporto} onChange={e => setCondoConfig({ ...condoConfig, praticaImporto: e.target.value })} />
                </div>
                <div>
                  <label className="label text-xs">Quota % immobile corrente</label>
                  <input type="number" step="0.01" className="input" value={condoConfig.quotaPercentuale} onChange={e => setCondoConfig({ ...condoConfig, quotaPercentuale: e.target.value })} />
                </div>
              </div>
            )}
            {condoConfig.praticaModo === 'esistente' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label text-xs">Pratica USR esistente</label>
                  <select
                    className="input"
                    value={condoConfig.praticaEsistenteId}
                    onChange={e => setCondoConfig({ ...condoConfig, praticaEsistenteId: e.target.value })}
                  >
                    <option value="">-- Seleziona --</option>
                    {praticheDisponibili.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.numero_pratica}{p.gruppo_nome ? ` (${p.gruppo_nome})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label text-xs">Quota % immobile corrente</label>
                  <input type="number" step="0.01" className="input" value={condoConfig.quotaPercentuale} onChange={e => setCondoConfig({ ...condoConfig, quotaPercentuale: e.target.value })} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stato contrattuale */}
      <div className="card p-5">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Stato contrattuale</h2>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="label">Stato</label>
            <select className="input" value={form.stato_contrattuale as string} onChange={e => handleChange('stato_contrattuale', e.target.value)}>
              <option value="trattativa">Trattativa</option>
              <option value="compromesso">Compromesso</option>
              <option value="rogito">Rogito</option>
            </select>
          </div>
          <Field label="Scadenza compromesso" field="scadenza_compromesso" type="date" form={form} onChange={handleChange} />
          <Field label="Data compromesso" field="data_compromesso" type="date" form={form} onChange={handleChange} />
          <Field label="Data rogito" field="data_rogito" type="date" form={form} onChange={handleChange} />
          <div>
            <label className="label">Notaio</label>
            <div className="flex gap-2">
              <select className="input" value={form.notaio_id as string} onChange={e => handleChange('notaio_id', e.target.value)}>
                <option value="">-- Seleziona --</option>
                {notai.map(n => <option key={n.id} value={n.id}>{n.nome}</option>)}
              </select>
              <button type="button" onClick={() => setShowNuovoNotaio(true)} className="btn-secondary text-sm whitespace-nowrap px-3">+ Nuovo</button>
            </div>
          </div>
        </div>
      </div>

      {/* Economico */}
      <div className="card p-5">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Dati economici</h2>
        <div className="grid grid-cols-4 gap-4">
          <Field label="Prezzo acquisto (€)" field="prezzo_acquisto" type="number" form={form} onChange={handleChange} />
          <Field label="Acconto (€)" field="acconto_pagato" type="number" form={form} onChange={handleChange} />
          <Field label="Scadenza" field="data_acconto" type="date" form={form} onChange={handleChange} />
          <Field label="Pagato il" field="acconto_pagato_il" type="date" form={form} onChange={handleChange}
            hint="Richiede la distinta acconto già caricata" />
        </div>
        <div className="grid grid-cols-3 gap-4 mt-4">
          <Field label="Residuo (€)" field="saldo_residuo" type="number" form={form} onChange={handleChange} disabled
            hint="Calcolato automaticamente: prezzo acquisto − acconto" />
          <Field label="Scadenza residuo" field="data_saldo" type="date" form={form} onChange={handleChange} />
          <Field label="Saldato il" field="saldo_effettuato_il" type="date" form={form} onChange={handleChange}
            hint="Richiede la distinta saldo già caricata" />
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <Field label="Totale imposte (€)" field="totale_imposte" type="number" form={form} onChange={handleChange} />
          <Field label="Totale spese notarili (€)" field="totale_spese_notarili" type="number" form={form} onChange={handleChange} />
        </div>
        <div className="flex gap-3 mt-3">
          <button type="button" onClick={() => openIndicatorModal(VOCE_DISTINTA_ACCONTO)} className="btn-secondary text-sm">
            📎 Allega distinta acconto{form.acconto_pagato_il && <span className="text-red-500"> *</span>}
          </button>
          <button type="button" onClick={() => openIndicatorModal(VOCE_DISTINTA_SALDO)} className="btn-secondary text-sm">
            📎 Allega distinta saldo{form.saldo_effettuato_il && <span className="text-red-500"> *</span>}
          </button>
        </div>
      </div>

      {/* Progettazione / USR */}
      <div className="card p-5">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Progettazione e USR</h2>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="label">Livello operativo</label>
            <select className="input" value={form.livello_operativo as string} onChange={e => handleChange('livello_operativo', e.target.value)}>
              <option value="">--</option>
              <option value="P1">P1 - Rilievo e documentazione (Stato di fatto, rilievi, schede FAST/AEDES)</option>
              <option value="P2">P2 - Progettazione preliminare (Progetto architettonico, strutturale, acustica)</option>
              <option value="P3">P3 - Progettazione esecutiva (CME, QTE, pratiche edilizie, permessi)</option>
              <option value="P4">P4 - Esecuzione lavori (Appalto, SAL, collaudo, agibilità)</option>
            </select>
          </div>
          <div>
            <label className="label mb-1 block text-red-600 font-semibold">Livello di Danno</label>
            <div className="flex gap-2">
              {(['L1', 'L2', 'L3', 'L4'] as const).map(l => (
                <button
                  key={l}
                  type="button"
                  onClick={() => handleChange('livello_danno', form.livello_danno === l ? '' : l)}
                  className={`w-11 h-11 rounded-md border-2 font-bold text-sm transition-colors ${
                    form.livello_danno === l
                      ? 'bg-red-600 border-red-600 text-white'
                      : 'bg-white border-red-300 text-red-600 hover:bg-red-50'
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label">Ufficio tecnico</label>
            <div className="flex gap-2">
              <select className="input" value={form.ufficio_tecnico_id as string} onChange={e => handleChange('ufficio_tecnico_id', e.target.value)}>
                <option value="">-- Seleziona --</option>
                {tecnici.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
              </select>
              <button type="button" onClick={() => setShowNuovoUfficioTecnico(true)} className="btn-secondary text-sm whitespace-nowrap px-3">+ Nuovo</button>
            </div>
          </div>
          <Field label="Codice fascicolo" field="codice_fascicolo" form={form} onChange={handleChange} />
          <Field label="Protocollo USR" field="protocollo_usr" form={form} onChange={handleChange} />
          <Field label="Delibera" field="delibera" form={form} onChange={handleChange} />
          <Field label="Decreto contributo" field="decreto_contributo" form={form} onChange={handleChange} />
          <Field label="Importo contributo (€)" field="importo_contributo" type="number" form={form} onChange={handleChange} />
          <Field label="Istruttore pratica" field="istruttore_pratica" form={form} onChange={handleChange} />
          <div>
            <label className="label">Privato/Condominio</label>
            <select className="input" value={form.privato_o_condominio as string} onChange={e => handleChange('privato_o_condominio', e.target.value)}>
              <option value="">--</option>
              <option value="privato">Privato</option>
              <option value="condominio">Condominio</option>
            </select>
          </div>
          <Field label="Nome condominio" field="nome_condominio" form={form} onChange={handleChange} />
        </div>
        <div className="grid grid-cols-4 gap-3 mt-4">
          {FLAG_PROGETTAZIONE_USR.map(voce => (
            <CheckFieldDoc
              key={voce.field}
              label={voce.label}
              field={voce.field as string}
              form={form}
              onChange={handleFlagChange}
              stato={form[voce.field as string] === 'true' ? (documentiPerTipologia(voce.tipologia).length > 0 ? 'presente' : 'mancante') : undefined}
              onIndicatorClick={() => openIndicatorModal(voce)}
            />
          ))}
        </div>
      </div>

      {modalVoce && (
        <UploadDocumentoFlagModal
          immobileId={immobile.id}
          fase={immobile.fase_corrente}
          voce={modalVoce}
          documentiEsistenti={modalDocumentiEsistenti}
          onClose={() => { setModalVoce(null); setModalDocumentiEsistenti([]) }}
          onUploaded={() => { loadDocumenti(); setModalDocumentiEsistenti(documentiPerTipologia(modalVoce.tipologia)); onUpdate() }}
        />
      )}

      {/* Pratica USR */}
      <div className="card p-5">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Pratica USR</h2>
        <SelectPraticaUSR
          gruppo_id={gruppo?.id}
          value={praticaValue}
          onChange={setPraticaValue}
        />
      </div>

      {/* Professionisti associati */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Professionisti associati</h2>
          <button onClick={() => setShowAddProf(true)} className="btn-primary text-sm">+ Aggiungi</button>
        </div>
        {profAssoc.length === 0 && <p className="text-sm text-gray-400">Nessun professionista associato</p>}
        <div className="space-y-2">
          {profAssoc.map(assoc => (
            <div key={assoc.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
              <div>
                <span className="font-medium text-sm">{assoc.professionista?.nome}</span>
                <span className="text-xs text-gray-500 ml-2">({assoc.professionista?.tipo} · {assoc.ruolo_specifico || assoc.professionista?.ruolo})</span>
              </div>
              <button onClick={() => handleRemoveProf(assoc.id)} className="text-red-500 text-xs hover:underline">Rimuovi</button>
            </div>
          ))}
        </div>
        {showAddProf && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg space-y-3">
            <div>
              <label className="label">Professionista</label>
              <div className="flex gap-2">
                <select className="input" value={selectedProf} onChange={e => setSelectedProf(e.target.value)}>
                  <option value="">-- Seleziona --</option>
                  {professionisti.map(p => <option key={p.id} value={p.id}>{p.nome} ({p.tipo})</option>)}
                </select>
                <button type="button" onClick={() => setShowNuovoProfessionista(true)} className="btn-secondary text-sm whitespace-nowrap px-3">+ Nuovo professionista</button>
              </div>
            </div>
            <Field label="Ruolo specifico" field="ruolo" form={{ ruolo: selectedRuolo }} onChange={(_, v) => setSelectedRuolo(v)} />
            <div className="flex gap-2">
              <button onClick={handleAddProf} className="btn-primary text-sm">Conferma</button>
              <button onClick={() => setShowAddProf(false)} className="text-sm text-gray-500">Annulla</button>
            </div>
          </div>
        )}
      </div>

      {showNuovoUfficioTecnico && (
        <NuovoProfessionistaModal
          tipoIniziale="tecnico"
          tipoBloccato
          onClose={() => setShowNuovoUfficioTecnico(false)}
          onCreated={handleUfficioTecnicoCreato}
        />
      )}

      {showNuovoProfessionista && (
        <NuovoProfessionistaModal
          tipoIniziale="tecnico"
          onClose={() => setShowNuovoProfessionista(false)}
          onCreated={handleProfessionistaCreato}
        />
      )}

      {showNuovoNotaio && (
        <NuovoProfessionistaModal
          tipoIniziale="notaio"
          tipoBloccato
          onClose={() => setShowNuovoNotaio(false)}
          onCreated={handleNotaioCreato}
        />
      )}

      {/* Riquadro Costi Immobile */}
      <div className="card p-5 bg-amber-50 border-amber-200">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Costi Immobile</h2>
        {(form.costi_extra as CostoExtraRow[]).length > 0 && (
          <div className="flex gap-2 mb-1 px-1">
            <span className="label flex-1">Tipo</span>
            <span className="label w-40">Importo</span>
            <span className="w-8" />
          </div>
        )}
        <div className="space-y-2">
          {(form.costi_extra as CostoExtraRow[]).map((riga, idx) => (
            <div key={riga.id} className="flex gap-2 items-center">
              <input
                className="input flex-1"
                placeholder="Tipo (es. Assicurazione, Utenze...)"
                value={riga.voce}
                onChange={e => {
                  const next = [...(form.costi_extra as CostoExtraRow[])]
                  next[idx] = { ...next[idx], voce: e.target.value }
                  handleChange('costi_extra', next)
                }}
              />
              <input
                className="input w-40 no-spinner"
                type="number"
                placeholder="Importo (€)"
                value={riga.importo || ''}
                onChange={e => {
                  const next = [...(form.costi_extra as CostoExtraRow[])]
                  next[idx] = { ...next[idx], importo: Number(e.target.value) }
                  handleChange('costi_extra', next)
                }}
              />
              <button
                type="button"
                onClick={() => handleChange('costi_extra', (form.costi_extra as CostoExtraRow[]).filter((_, i) => i !== idx))}
                className="text-red-500 hover:text-red-700 p-2"
                title="Rimuovi voce"
              >
                🗑
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => handleChange('costi_extra', [...(form.costi_extra as CostoExtraRow[]), { id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, voce: '', importo: 0 }])}
          className="btn-secondary text-sm mt-3"
        >
          + Aggiungi voce
        </button>
        {(form.costi_extra as CostoExtraRow[]).length > 0 && (
          <p className="text-sm text-gray-500 mt-3">
            Totale costi annui: <strong>{EUR.format((form.costi_extra as CostoExtraRow[]).reduce((s, r) => s + (Number(r.importo) || 0), 0))}</strong>
          </p>
        )}
      </div>

      {/* Note */}
      <div className="card p-5">
        <label className="label">Note</label>
        <textarea
          className="input"
          rows={3}
          value={form.note as string}
          onChange={e => handleChange('note', e.target.value)}
        />
      </div>

      <div className="flex items-center gap-3">
        <button onClick={handleSave} disabled={saving} className="btn-primary">
          {saving ? 'Salvataggio...' : 'Salva anagrafica'}
        </button>
        <button
          onClick={async () => {
            if (!confirm('Sei sicuro di voler eliminare questo immobile? Verranno cancellati anche tutti i documenti, pagamenti, appalto, SAL e scadenze collegati.')) return
            try {
              await redApi.immobili.delete(immobile.id)
              toast.success('Immobile eliminato')
              navigate('/red')
            } catch {
              toast.error('Errore eliminazione immobile')
            }
          }}
          className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          🗑 Elimina immobile
        </button>
      </div>
    </div>
  )
}

export default TabAnagrafica
